import React, { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  Card,
  CardBody,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  UncontrolledDropdown,
  UncontrolledTooltip,
} from "reactstrap"

import Breadcrumbs from "components/Common/BreadcrumbTitle"
import AppConstant from "../../constants/app.constants"

import DeleteModal from "components/Common/DeleteModal"
import ToastDialog from "components/Common/ToastDialog"
import { AiOutlineSync } from "react-icons/ai"
import TableContainerV1 from "components/Common/TableContainerV1"

import {
  getSubCategoryList,
  deleteSubCategory,
} from "helpers/UAM/subCategory.helper"
import { getGeneralMaster } from "http/master/master"
import { formatDateTime, isPermission } from "common/utility"
import { getApiLogs } from "http/apiLogs/apiLogs"
import FullPageLoader from "components/Common/FullPageLoader"

const TableActionsComponent = (rowData) => {
  const navigate = useNavigate()
  const {roleName} = JSON.parse(localStorage.getItem("user"))
  const navigateToeViewSubCategory = id => {
    const encryptedId = btoa(id.toString())
    navigate(`/${roleName}/sub-category/view?id=${encodeURIComponent(encryptedId)}`)
  }

  return (
    <UncontrolledDropdown>
      <DropdownToggle tag="a" className="card-drop">
        <i className="mdi mdi-dots-horizontal font-size-18"></i>
      </DropdownToggle>

      <DropdownMenu className="dropdown-menu-end">
        <DropdownItem
          onClick={() => {
            navigateToeViewSubCategory(rowData.id)
          }}
        >
          <i
            className="mdi mdi-eye font-size-16 text-success me-1"
            id="edittooltip"
          ></i>
          View
          <UncontrolledTooltip placement="top" target="edittooltip">
            View
          </UncontrolledTooltip>
        </DropdownItem>
      </DropdownMenu>
    </UncontrolledDropdown>
  )
}

const SubCategory = props => {
  document.title = "SubCategory List | DMS"
  const [subCategoryList, setSubCategoryList] = useState([])
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageCount: 0,
    totalRecords: 0,
  })
  const [pageSize, setPageSize] = useState(10)
  const [searchValue, setSearchValue] = useState("")
  const [columnId, setColumnId] = useState("id")
  const [sortingOrderStatus, setSortingOrderStatus] = useState("desc")
  const [sortingOptions, setSortingOptions] = useState({
    column: "id",
    isAsc: false,
    optionalColumn: ["Action"],
  })
  const [deleteModal, setDeleteModal] = useState(false)
  const [deleteItemId, setDeleteItemId] = useState(null)
  const [toastInfo, setToastInfo] = useState({ showToast: false })
  const [isSyncing, setIsSyncing] = useState(false)
  const [lastSynced, setLastSynced] = useState(null);
  const [syncPermission, setSyncPermission] = useState(false);

  const columns = useMemo(
    () => [
      {
        Header: "Id",
        accessor: "id",
        filterable: true,
        Cell: cellProps => {
          return cellProps.value
        },
      },
      {
        Header: "SubCategory Code",
        accessor: "subCategoryCode",
        filterable: true,
        Cell: cellProps => {
          return cellProps.value
        },
      },
      {
        Header: "SubCategory Name",
        accessor: "subCategoryName",
        filterable: true,
        Cell: cellProps => {
          return cellProps.value
        },
      },
      {
        Header: "SubCategory Description",
        accessor: "subCategoryDescription",
        filterable: true,
        Cell: cellProps => {
          return cellProps.value
        },
      },
      {
        Header: "Action",
        Cell: cellProps => {
          const rowData = cellProps.row.original
          return TableActionsComponent(rowData)
        },
      },
    ],
    []
  )

  const handleDeleteSubCategory = id => {
    setDeleteItemId(id)
    setDeleteModal(true)
  }

  const deleteSubCategoryHelper = () => [
    deleteSubCategory({ id: deleteItemId }).then(response => {
      if (response.status && response.code == 201) {
        setDeleteModal(false)
        fetchSubCategoryData()
      }
    }),
  ]

  useEffect(() => {
    fetchSubCategoryData()
    fetchLastSynced()
    checkPermission()
  }, [])

  const checkPermission = async () => {
    let isSyncPermission = await isPermission('sync', 'create_permission')
    setSyncPermission(isSyncPermission)
  }

  const fetchSubCategoryData = () => {
    getSubCategoryList({ page: 1, limit: AppConstant.TABLE_RECORD_SIZE })
      .then(responseFromAPI => {
        if (responseFromAPI.status && responseFromAPI.code == 200) {
          setSubCategoryList(responseFromAPI.data.data)
          const { totalItems, totalPages, currentPage } = responseFromAPI.data
          setPagination({
            pageIndex: currentPage,
            pageCount: totalPages,
            totalRecords: totalItems,
          })
        }
      })
      .catch(error => { })
  }

  const handlePageChange = (
    page,
    pageSize,
    searchKey = searchValue,
    columnName = columnId,
    sortingOrder = sortingOrderStatus
  ) => {
    setPageSize(pageSize)
    getSubCategoryList({
      page,
      limit: pageSize,
      searchKey,
      columnName,
      sortingOrder,
    })
      .then(responseFromAPI => {
        if (responseFromAPI.status && responseFromAPI.code == 200) {
          setSubCategoryList(responseFromAPI.data.data)
          const { totalItems, totalPages, currentPage } = responseFromAPI.data
          setPagination({
            pageIndex: currentPage,
            pageCount: totalPages,
            totalRecords: totalItems,
          })
        } else {
          setPagination({ pageIndex: 0, pageCount: 0, totalRecords: 0 })
          setSubCategoryList([])
        }
      })
      .catch(error => { })
  }

  const handleSortColumn = option => {
    setSortingOptions(option)
    const columnName = option.column
    setColumnId(columnName)
    const sortingOrder = option.isAsc ? "asc" : "desc"
    setSortingOrderStatus(sortingOrder)
    handlePageChange(1, pageSize, searchValue, columnName, sortingOrder)
  }

  const handleSearchFilter = (value, pageSize) => {
    const newValue = value !== undefined ? value : ""
    setSearchValue(newValue)
    handlePageChange(1, pageSize, newValue)
  }
  const handleClearSearch = () => {
    setSearchValue("")
    handlePageChange(1, pageSize, "", "id", "desc")
  }

  const handleSync = async () => {
    setIsSyncing(true)
    try {
      let res = await getGeneralMaster({ masterName: "subcategory" })
      if (res.status) {
        setToastInfo({
          message: res.message,
          showToast: true,
          role: "success",
        })
        fetchSubCategoryData()
        setIsSyncing(false)
        fetchLastSynced()
      }
    } catch (err) {
      setToastInfo({
        message: "Something went wrong",
        showToast: true,
        role: "error",
      })
      setIsSyncing(false)
    }
  }

  const fetchLastSynced = async () => {
    try {
      let res = await getApiLogs({ masterName: 'subcateogry_master' })
      if (res.status) {
        setLastSynced(formatDateTime(res.data.initialDateTime))
      }
    } catch (err) {
      console.error(err)
    }
  }
  const buttonSection = (
    <>
      {syncPermission && (<div>
        <div className="d-flex justify-content-end mb-1">
          <button
            className="btn btn-success"
            type="button"
            onClick={handleSync}
          >
            <AiOutlineSync size={15} className={isSyncing ? 'rotating' : ''} /> Sync Data
          </button>
        </div>
        {lastSynced && <p className="d-flex justify-content-end ">Last Synced: {lastSynced}</p>}
      </div>)}
    </>

  )

  return (
    <React.Fragment>
      {isSyncing && <FullPageLoader />}
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Sub Category List" />
          <Row className="vertical-scroll">
            <Col xs="12">
              <Card>
                <CardBody>
                  <TableContainerV1
                    columns={columns}
                    data={subCategoryList}
                    pagination={pagination}
                    handlePageChange={handlePageChange}
                    searchHelperText="Search by Sub Category Name and Sub Category Code"
                    handleSearchFilter={handleSearchFilter}
                    sortOptions={sortingOptions}
                    customSection={buttonSection}
                    handleSortColumn={handleSortColumn}
                    handleClearSearch={handleClearSearch}
                    className="custom-header-css"
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={deleteSubCategoryHelper}
        onCloseClick={() => setDeleteModal(false)}
      />
      <ToastDialog
        message={toastInfo.message}
        showToast={toastInfo.showToast}
        role={toastInfo.role}
        onCloseFn={() => setToastInfo({ showToast: false })}
      />
    </React.Fragment>
  )
}

export default SubCategory
